import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { Chip, InputAdornment, InputProps } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { WithStyles } from "@mui/styles";
import createStyles from "@mui/styles/createStyles";
import withStyles from "@mui/styles/withStyles";
import { violetDeep } from "@shared/designSystem/colors";
import classnames from "classnames";
import Downshift, { DownshiftState, StateChangeOptions } from "downshift";
import isEqual from "lodash/isEqual";
import React, { SyntheticEvent } from "react";
import { ISelectOption, optionKey } from "shared/components/ISelectOption";
import TextField, { TextFieldProps } from "shared/components/TextField";
import { TranslationProps, withTranslations } from "shared/utilities/I18nContext";
import { styles as baseStyles, getOptions, itemToString, OptionMenu, removeDuplicateValues } from "./Shared";

const styles = (theme: Theme) =>
  createStyles({
    singleLineInputContainer: {
      flexWrap: "nowrap",
      "& input": {
        width: "auto",
        padding: theme.spacing(2),
      },
    },
    singleLineEndAdornmentContainer: {
      width: "100%",
      display: "flex",
      flexDirection: "row",
      justifyContent: "flex-end",
      alignItems: "center",
    },
    inputContainer: {
      flexWrap: "wrap",
    },
    chipContainer: {
      paddingTop: theme.spacing(1),
      maxWidth: "100%",
    },
    noWrap: {
      display: "flex",
      flexWrap: "nowrap",
    },
    chip: {
      margin: "0 4px 2px 0",
      height: `calc(2 * ${theme.typography.button.fontSize})`,
    },
    invalidChip: {
      backgroundColor: theme.palette.error.light,
      color: theme.palette.error.contrastText,
      "&:focus": {
        backgroundColor: theme.palette.error.main,
      },
    },
    chipWithLink: {
      "&:hover": {
        "& .MuiChip-label": {
          textDecoration: "underline",
        },
      },
    },
    chipAction: {
      backgroundColor: "transparent",
      color: violetDeep,
      cursor: "pointer",
      "&:hover": {
        backgroundColor: "transparent",
      },
      "&:focus": {
        backgroundColor: "transparent",
      },
    },
    deletable: {
      maxWidth: "100%",
      display: "inline-flex",
    },
    endAdornmentContainer: {
      width: "100%",
      display: "flex",
      flexDirection: "row",
      justifyContent: "flex-end",
      alignItems: "center",
      marginBottom: theme.spacing(2),
    },
    anchor: {
      position: "absolute",
      bottom: 0,
      left: 0,
      width: "100%",
      height: 0,
      pointerEvents: "none",
    },
    ...baseStyles(theme),
  });

interface IMaterialAutocompleteOption extends ISelectOption {
  isValid: boolean;
}

export const selectAllOption = Object.freeze({ label: "Select All", value: "__select_all__" });

export interface IMaterialAutocompleteMultipleProps extends Omit<
  TextFieldProps,
  "classes" | "onChange" | "value" | "defaultValue"
> {
  options: ISelectOption[];
  values: any[];
  onChange: (values: any) => void;
  endAdornment?: React.ReactNode;
  inputTitle?: string;
  selectedShowLimit?: number;
}

interface IMaterialAutocompleteMultipleState {
  query: string;
  hasFocus: boolean;
  openedByClick: boolean;
  chipsExpanded: boolean;
}

class MaterialAutocompleteMultiple extends React.Component<
  IMaterialAutocompleteMultipleProps & WithStyles<typeof styles, true> & TranslationProps,
  IMaterialAutocompleteMultipleState
> {
  private popperNode: React.RefObject<HTMLDivElement>;
  private searchInputRef: React.RefObject<HTMLInputElement> = React.createRef();
  private anchorRef: React.RefObject<HTMLDivElement> = React.createRef();

  constructor(props: IMaterialAutocompleteMultipleProps & WithStyles<typeof styles, true> & TranslationProps) {
    super(props);
    this.popperNode = React.createRef<HTMLDivElement>();
    this.state = {
      query: "",
      hasFocus: false,
      openedByClick: false,
      chipsExpanded: false,
    };
  }

  public componentDidUpdate() {
    this.collapseIfShrankToLimit();
  }

  private collapseIfShrankToLimit() {
    if (!this.state.chipsExpanded) return;

    const limit = Math.max(0, this.props.selectedShowLimit ?? 0);

    const valuesShrankToLimit = limit > 0 && this.props.values.length <= limit;

    if (valuesShrankToLimit) {
      this.setState({ chipsExpanded: false });
    }
  }

  public render() {
    const { handleChange, renderInput } = this;
    const {
      id,
      placeholder,
      classes,
      options: allOptions,
      theme,
      values,
      fullWidth,
      endAdornment,
      inputTitle,
      disabled,
      selectedShowLimit,
      ...restProps
    } = this.props;

    const options = removeDuplicateValues(allOptions);

    const selectedOptions = this.getSelectedOptions();

    const withSearchBar = !!inputTitle;
    return (
      <Downshift
        id={id}
        selectedItem={selectedOptions}
        onChange={handleChange}
        itemToString={itemToString}
        defaultHighlightedIndex={0}
        stateReducer={stateReducer}
      >
        {({
          getInputProps,
          getItemProps,
          getMenuProps,
          isOpen,
          selectedItem: currentlySelectedItem,
          highlightedIndex,
          openMenu,
          closeMenu,
          setItemCount,
          setHighlightedIndex,
        }) => {
          const suggestedOptions = getOptions({
            query: this.state.query,
            options,
            selectedValues: values,
            includeSelectAll: options.length > 1,
          });
          const rowCount = suggestedOptions.length;
          setItemCount(rowCount);
          if (highlightedIndex! >= rowCount && rowCount > 0) {
            setHighlightedIndex(0);
          }

          const rowHeight = theme!.typography.fontSize * 3;
          const onFocus = () => {
            this.setState(state => (!state.hasFocus ? { hasFocus: true } : null));
            openMenu();
          };
          const onBlur = () => {
            this.setState(state => (state.hasFocus ? { hasFocus: false, openedByClick: false } : null));
          };

          const onClick = () => {
            if (this.state.openedByClick && isOpen) {
              this.setState({ openedByClick: false, hasFocus: false });
              closeMenu();
            } else {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              this.setState(state => ({ hasFocus: true, openedByClick: true }));
              openMenu();
            }
          };

          const checkBoxProps = {
            withCheckbox: true,
            onSearch: this.handleInputChange,
            allSelected: values.length === options.filter(option => !option.disabled).length,
            getInputProps,
            searchQuery: this.state.query,
            searchInputRef: this.searchInputRef,
          };

          return (
            <div className={fullWidth ? classes.fullWidthContainer : classes.container}>
              <div ref={this.anchorRef} className={classes.anchor} />
              {renderInput(selectedOptions, {
                fullWidth,
                ...restProps,
                InputProps: getInputProps({
                  onClick,
                  onFocus,
                  onBlur,
                  id,
                  placeholder,
                  onKeyDown: this.handleKeyDown.bind(this),
                }) as InputProps,
                ref: this.popperNode,
              })}
              <OptionMenu
                {...{
                  isOpen: !disabled && isOpen,
                  rowCount,
                  rowHeight,
                  highlightedIndex,
                  getItemProps,
                  getMenuProps,
                  anchorNode: this.anchorRef.current ?? this.popperNode.current,
                  selectedItemKey: currentlySelectedItem,
                  options: suggestedOptions,
                  withSearchBar,
                  checkBoxProps,
                }}
              />
            </div>
          );
        }}
      </Downshift>
    );
  }

  private handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const { query } = this.state;
    const selectedOptions = this.getSelectedOptions();

    if (selectedOptions.length && !query.length && event.key === "Backspace") {
      const values = selectedOptions.slice(0, selectedOptions.length - 1).map(o => o.value);
      this.props.onChange(values);
    } else if (event.key === "Tab" && this.searchInputRef.current) {
      event.preventDefault();
      this.searchInputRef.current.focus();
    }
  };

  private handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ query: event.target.value });
  };

  private handleChange = (option: ISelectOption | ISelectOption[]) => {
    const { values, onChange, options } = this.props;
    const changedOption = Array.isArray(option) ? option[0] : option;

    if (changedOption.value === selectAllOption.value) {
      this.handleSelectAll(values, options, onChange);
    } else {
      this.handleSingleOption(changedOption, values, onChange);
    }
  };

  private handleSelectAll = (selectedValues: any[], allOptions: ISelectOption[], onChange: (values: any[]) => void) => {
    const availableOptions = allOptions.filter(option => !option.disabled);
    const allAvailableOptionsSelected = availableOptions.length === selectedValues.length;

    if (allAvailableOptionsSelected) {
      onChange([]);
    } else {
      const newSelectedValues = availableOptions.map(option => option.value);
      onChange([...newSelectedValues]);
    }
  };

  private handleSingleOption = (option: ISelectOption, currentValues: any[], onChange: (values: any[]) => void) => {
    const newValue = option.value;
    const isSelected = currentValues.includes(newValue);

    if (!isSelected) {
      onChange([...currentValues, newValue]);
    } else {
      const newValues = currentValues.filter(value => value !== newValue);
      onChange(newValues);
    }
  };

  private handleDelete = (option: ISelectOption) => () => {
    const { values } = this.props;

    const newValues = values.filter(value => !isEqual(optionKey(option.value), optionKey(value)));
    this.props.onChange(newValues);
  };

  private handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    const pastedText = event.clipboardData.getData("Text");
    if (pastedText.indexOf(",") === -1) return;

    event.preventDefault();

    const { options, onChange } = this.props;
    const values = [...this.props.values];

    const pastedStrings = pastedText.split(",").map(value => value.trim());

    const pastedValues = pastedStrings.map(pastedString => {
      const option = options.find(({ label, value }) => pastedString === value || pastedString === label);

      if (option) return option.value;

      return pastedString;
    });

    pastedValues.forEach(pastedValue => {
      if (values.indexOf(pastedValue) === -1) values.push(pastedValue);
    });

    onChange(values);
  };

  private renderInput = (
    selectedOptions: IMaterialAutocompleteOption[],
    textFieldProps: TextFieldProps & { ref: React.Ref<HTMLDivElement> },
  ) => {
    const { label, InputProps, InputLabelProps, ref, variant, ...other } = textFieldProps;
    const { classes, disabled, endAdornment, inputTitle, selectedShowLimit = 0 } = this.props;
    const { query, hasFocus, chipsExpanded } = this.state;

    const isFieldRequired = textFieldProps.required && selectedOptions.length === 0;

    if (inputTitle) {
      return (
        <TextField
          InputProps={{
            inputRef: ref,
            ...InputProps,
            value: inputTitle,
            sx: { caretColor: "transparent" },
            endAdornment: (
              <InputAdornment position="end">
                <ArrowDropDownIcon />
              </InputAdornment>
            ),
            required: isFieldRequired,
          }}
          label={label}
          variant={variant}
          {...other}
        />
      );
    }

    const normalizedLimit = Math.max(0, selectedShowLimit);
    const singleLine = normalizedLimit > 0 && !chipsExpanded;

    const chipsToDisplay = this.renderChips(selectedOptions);

    const chipContainer =
      chipsToDisplay.length > 0 ? (
        <div
          className={classnames(classes.chipContainer, {
            [classes.noWrap]: singleLine,
          })}
        >
          {chipsToDisplay}
        </div>
      ) : null;

    const renderEndAdornment = () => {
      if (!endAdornment) return null;
      return (
        <div className={singleLine ? classes.singleLineEndAdornmentContainer : classes.endAdornmentContainer}>
          {endAdornment}
        </div>
      );
    };

    return (
      <TextField
        onPaste={this.handlePaste}
        InputProps={{
          inputRef: ref,
          className: singleLine ? classes.singleLineInputContainer : classes.inputContainer,
          startAdornment: chipContainer,
          endAdornment: renderEndAdornment(),
          ...InputProps,
          value: query,
          onChange: this.handleInputChange,
          required: isFieldRequired,
        }}
        label={label}
        disabled={disabled}
        InputLabelProps={{
          shrink: chipsToDisplay.length > 0 || query.length > 0 || hasFocus,
          ...InputLabelProps,
        }}
        variant={variant}
        {...other}
      />
    );
  };

  private renderChips(selectedOptions: IMaterialAutocompleteOption[]) {
    const { classes, disabled, i18n, selectedShowLimit } = this.props;
    const { chipsExpanded } = this.state;

    const normalizedLimit = Math.max(0, selectedShowLimit || 0);
    const hasChipLimit = normalizedLimit > 0;
    const shouldTruncate = hasChipLimit && !chipsExpanded && selectedOptions.length > normalizedLimit;

    const optionsToDisplay = shouldTruncate ? selectedOptions.slice(0, normalizedLimit) : selectedOptions;

    const chips = optionsToDisplay.map(option => {
      return (
        <Chip
          key={optionKey(option.value)}
          label={option.label}
          onClick={option.link ? () => window.open(option.link) : undefined}
          onDelete={disabled ? undefined : this.handleDelete(option)}
          className={classnames(
            classes.chip,
            { [classes.invalidChip]: !option.isValid, [classes.chipWithLink]: option.link },
            classes.deletable,
          )}
        />
      );
    });

    if (shouldTruncate) {
      const hiddenChipCount = selectedOptions.length - normalizedLimit;
      return [
        ...chips,
        <Chip
          key="more-chips"
          label={i18n.translations.materialAutocompleteMultiple.showMoreLabel(hiddenChipCount)}
          onClick={disabled ? undefined : this.handleExpandChips}
          className={classnames(classes.chip, classes.chipWithLink, classes.chipAction)}
        />,
      ];
    }

    if (hasChipLimit && chipsExpanded && chips.length > normalizedLimit) {
      return [
        ...chips,
        <Chip
          key="see-less-chips"
          label={i18n.translations.materialAutocompleteMultiple.seeLessLabel}
          onClick={disabled ? undefined : this.handleCollapseChips}
          className={classnames(classes.chip, classes.chipWithLink, classes.chipAction)}
        />,
      ];
    }

    return chips;
  }

  private getSelectedOptions = () => {
    const { options, values } = this.props;
    const selectedOptions: IMaterialAutocompleteOption[] = [];

    values.forEach(value => {
      const selectedOption = options.find(o => optionKey(o.value) === optionKey(value));

      if (selectedOption) {
        selectedOptions.push({ isValid: true, ...selectedOption });
      } else {
        selectedOptions.push({ value, label: optionKey(value).toString(), isValid: false });
      }
    });
    return selectedOptions;
  };

  private handleExpandChips = (event: SyntheticEvent) => {
    event.stopPropagation();
    this.setState({ chipsExpanded: true });
  };

  private handleCollapseChips = (event: SyntheticEvent) => {
    event.stopPropagation();
    this.setState({ chipsExpanded: false });
  };
}

function stateReducer(state: DownshiftState<ISelectOption>, changes: StateChangeOptions<ISelectOption>) {
  // this prevents the menu from being closed when the user
  // selects an item with a keyboard or mouse
  switch (changes.type) {
    case Downshift.stateChangeTypes.keyDownEnter:
    case Downshift.stateChangeTypes.clickItem:
    case Downshift.stateChangeTypes.blurInput:
      return {
        ...changes,
        isOpen: state.isOpen,
        highlightedIndex: state.highlightedIndex,
      };
    default:
      return changes;
  }
}

export default withTranslations(withStyles(styles, { withTheme: true })(MaterialAutocompleteMultiple));
